import {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";

import useWexClient from "../../../hooks/Clients/wexClient,";
import { AuthContext } from "../../../Auth/Context";

import {
  Stack,
  CircularProgress,
  Box,
  Button,
  ButtonGroup,
  IconButton,
} from "@mui/material";
import IconWithTitle from "../../../components/atoms/IconWithTitle";
import { EqualizerRounded } from "@mui/icons-material";

import "../../../styles/App.css";

import { useTranslation } from "react-i18next";

function ReportKPI({ selectedOption }) {
  const payload = useContext(AuthContext);
  const { t } = useTranslation();

  const service = payload?.payload?.service;

  const { getTokenKPI, getOptionsKPI } = useWexClient();

  const [embedConfig, setEmbedConfig] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🔹 Estados para la navegación personalizada
  const [reportInstance, setReportInstance] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePageName, setActivePageName] = useState("");
  const reportRef = useRef(null);

  // 🔹 Obtener opciones KPI
  const fetchOptions = async () => {
    try {
      const response = await getOptionsKPI(service);

      if (response?.success) {
        setKpiData(response);
      }
    } catch (error) {
      console.log("error fetching options", error.message);
    }
  };

  // 🔹 Obtener token Power BI
  const fetchToken = async () => {
    try {
      setLoading(true);

      const response = await getTokenKPI(service);

      setEmbedConfig({
        type: "report",
        id: response.reportId,
        embedUrl: response.embedUrl,
        accessToken: response.embedToken,
        tokenType: models.TokenType.Embed,
        settings: {
          navContentPaneEnabled: false,
          panes: {
            filters: {
              expanded: false,
              visible: false,
            },
          },
          background: models.BackgroundType.Transparent,
        },
      });
    } catch (error) {
      console.log("error fetching token", error.message);
      setLoading(false);
    }
  };

  // 🔹 Cargar data inicial
  useEffect(() => {
    if (!service) return;

    fetchOptions();
    fetchToken();
  }, [service]);

  // 🔹 Cuando cambia el KPI seleccionado
  useEffect(() => {
    if (selectedOption) {
      setLoading(true);
    }
  }, [selectedOption]);

  // 🔹 El fetching de páginas ahora se realiza de forma segura en el evento "loaded" del iframe de PowerBI

  // 🔹 Función para manejar el cambio de página desde el Frontend
  const handlePageChange = useCallback(
    async (pageName) => {
      if (reportInstance) {
        try {
          await reportInstance.setPage(pageName);
          setActivePageName(pageName);
        } catch (err) {
          console.error("Error changing page:", err);
        }
      }
    },
    [reportInstance],
  );

  // 🔹 Estabilizar props de PowerBIEmbed para evitar re-incrustaciones (re-embeds) infinitas
  const embedStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);

  const eventHandlers = useMemo(
    () =>
      new Map([
        [
          "loaded",
          async () => {
            console.log("PowerBI loaded");
            if (reportRef.current) {
              try {
                const reportPages = await reportRef.current.getPages();
                const visiblePages = reportPages.filter(
                  (p) => p.visibility === 0 || p.visibility === undefined,
                );
                setPages(visiblePages);

                if (visiblePages.length > 0) {
                  const firstPage = visiblePages[0];
                  await reportRef.current.setPage(firstPage.name);
                  setActivePageName(firstPage.name);
                }
              } catch (error) {
                console.error("Error fetching pages on loaded event:", error);
              }
            }
          },
        ],
        ["rendered", () => setLoading(false)],
        [
          "error",
          (event) => {
            console.error(event.detail);
            setLoading(false);
          },
        ],
        [
          "pageChanged",
          (event) => {
            const newPage = event.detail.newPage;
            if (newPage) {
              setActivePageName(newPage.name);
            }
          },
        ],
      ]),
    [setActivePageName],
  );

  const getEmbeddedComponent = useCallback((report) => {
    window.Report = report;
    reportRef.current = report;
    setReportInstance((prev) => (prev !== report ? report : prev));
  }, []);

  // 🔹 Obtener el nombre de la página activa para el título dinámico (con fallback a la primera página)
  const activePageDisplayName = useMemo(() => {
    console.log("Pages:", pages);
    console.log("Active Page Name:", activePageName);

    const activePage = pages.find((p) => p.name === activePageName);

    console.log("Active Page:", activePage);
    console.log("Fallback:", pages[0]);

    if (activePage) return activePage.displayName;

    if (pages.length > 0) return pages[0]?.displayName;

    return "Tablero KPI's Seguros Banorte";
  }, [pages, activePageName]);

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack
        direction="row"
        spacing={4}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: "3rem", marginBottom: "1rem" }}
      >
        <Stack>
          <IconWithTitle
            icon={<EqualizerRounded fontSize="large" />}
            title={activePageDisplayName}
            iconSize="40px"
          />
        </Stack>
      </Stack>

      <Stack
        direction="row"
        sx={{
          width: "100%",
          height: "75vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.4)",
              backdropFilter: "blur(2px)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* 🔹 CONTENEDOR PRINCIPAL DEL PANEL NAVEGACIÓN (Sidebar fijo) */}
        {pages.length > 0 && (
          <Box
            sx={{
              width: "240px",
              height: "100%",
              display: "flex",
              flexDirection: "row",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            {/* Barra lateral física con el contenido de páginas */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#fff",
                borderRight: "1px solid rgba(224, 224, 224, 0.6)",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Lista de páginas */}
              <Stack
                spacing={1}
                sx={{
                  padding: "16px 12px",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    pb: 1,
                    borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("Páginas", "Páginas")}
                  </Box>
                </Box>
                {pages.map((page) => (
                  <Button
                    key={page.name}
                    variant={
                      activePageName === page.name ? "contained" : "text"
                    }
                    onClick={() => handlePageChange(page.name)}
                    sx={{
                      textTransform: "none",
                      fontWeight:
                        activePageName === page.name ? "bold" : "normal",
                      justifyContent: "flex-start",
                      borderRadius: "6px",
                      px: 2,
                      py: 0.8,
                      fontSize: "0.85rem",
                      whiteSpace: "normal",
                      textAlign: "left",
                      transition: "all 0.2s ease",
                      backgroundColor:
                        activePageName === page.name
                          ? "grey.700"
                          : "transparent",
                      color:
                        activePageName === page.name
                          ? "#fff"
                          : "text.primary",
                      "&:hover": {
                        backgroundColor:
                          activePageName === page.name
                            ? "grey.800"
                            : "rgba(0, 0, 0, 0.04)",
                        transform: "translateX(2px)",
                      },
                    }}
                  >
                    {page.displayName}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Box>
        )}

        {/* Contenedor del PowerBIEmbed */}
        <Box sx={{ flexGrow: 1, height: "100%", overflow: "visible" }}>
          {embedConfig && (
            <PowerBIEmbed
              embedConfig={embedConfig}
              cssClassName="bi-embedded"
              style={embedStyle}
              eventHandlers={eventHandlers}
              getEmbeddedComponent={getEmbeddedComponent}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
}

export default ReportKPI;
